import React, { memo, useMemo, useCallback, useState, useContext } from 'react'
import tw from 'twin.macro'
import { useDispatch } from 'react-redux'
import { showToast } from 'src/utils/toast'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { handleApiError } from 'src/utils/errorHandlers'
import {
  ActivatePartnerDriver,
  DeactivatePartnerDriver,
  RejectOnboarding,
  UnrejectOnboarding,
  SendRemainderContractor,
  SendRemainderOnboarding,
} from 'src/constants/actionTypes'
import { ContractorDetails } from 'src/interfaces/contractor'
import { OnboardingStatus } from 'src/constants/onboarding/enums'
import { Button } from 'src/components/shared/Button'
import { RejectContractor } from './RejectContractor'
import { UnrejectContractor } from './UnrejectContractor'
import { ActivateContractor } from './ActivateContractor'
import { DeactivateContractor } from './DeactivateContractor'
import { SetDeactivationDate } from './SetDeactivationDate'
import { SendMajor } from '@shopify/polaris-icons'
import { colors } from 'src/theme'
import { ContractorDeactivationReason } from 'src/constants/contractor/enums'
import { ContractorDetailsModalContext } from '../ContractorDetailsModal.context'
import { Typography } from '@mui/material'
import dayjs from 'dayjs'

interface ContractorActionsProps {
  contractorDetails: ContractorDetails
  flow: 'onboardings' | 'contractors'
  isSubcontractor?: boolean
  onRefresh(): Promise<void> | void
  onClose(): void
}

const ContractorActions = memo(
  ({ contractorDetails, flow, onRefresh, onClose, isSubcontractor }: ContractorActionsProps) => {
    const dispatch = useDispatch()

    const { statusIsUpdating } = useContext(ContractorDetailsModalContext)

    const [isLoading, setIsLoading] = useState(false)

    const hasStepsWithRecollectingStatus = contractorDetails.collectedData.some(
      (item) => !!item.isRecollecting,
    )

    const shouldShowResendInviteButton = useMemo(() => {
      if (isSubcontractor) {
        return false
      }

      const { collectedDataStatus, deactivationReason } = contractorDetails

      if (
        collectedDataStatus === OnboardingStatus.Created ||
        collectedDataStatus === OnboardingStatus.PartiallyCompleted
      ) {
        return true
      }

      if (
        deactivationReason !== ContractorDeactivationReason.Manual &&
        hasStepsWithRecollectingStatus &&
        flow === 'contractors'
      ) {
        return true
      }

      return false
    }, [isSubcontractor, contractorDetails, hasStepsWithRecollectingStatus, flow])

    const handleRejectWorker = useCallback(async () => {
      setIsLoading(true)
      try {
        await createAsyncAction(
          dispatch,
          RejectOnboarding.request({ partnerDriverId: contractorDetails.id }),
        )
        showToast('Contractor rejected successfully!')
        await onRefresh()

        onClose()
      } catch (err) {
        handleApiError(err)
        showToast('Failed to reject contractor!')
      } finally {
        setIsLoading(false)
      }
    }, [contractorDetails, onRefresh])

    const handleUnrejectWorker = useCallback(async () => {
      try {
        await createAsyncAction(
          dispatch,
          UnrejectOnboarding.request({ partnerDriverId: contractorDetails.id }),
        )
        showToast('Contractor unrejected!')
        await onRefresh()
        onClose()
      } catch (err) {
        handleApiError(err)
      }
    }, [contractorDetails, onRefresh])

    const handleActivateWorker = useCallback(async () => {
      try {
        await createAsyncAction(
          dispatch,
          ActivatePartnerDriver.request({
            partnerDriverId: contractorDetails.id,
          }),
        )
        showToast('Contractor activated!')
        onRefresh()
      } catch (err) {
        handleApiError(err)
      }
    }, [contractorDetails, onRefresh])

    const handleDeactivateWorker = useCallback(async () => {
      try {
        await createAsyncAction(
          dispatch,
          DeactivatePartnerDriver.request({
            partnerDriverId: contractorDetails.id,
          }),
        )
        showToast('Contractor deactivated!')
        onRefresh()
      } catch (err) {
        handleApiError(err)
      }
    }, [contractorDetails, onRefresh])

    const handleResendInvite = useCallback(async () => {
      setIsLoading(true)

      try {
        if (
          contractorDetails.collectedDataStatus === OnboardingStatus.Created ||
          contractorDetails.collectedDataStatus === OnboardingStatus.PartiallyCompleted
        ) {
          await createAsyncAction(
            dispatch,
            SendRemainderOnboarding.request({
              partnerDriverId: contractorDetails.id,
            }),
          )
        } else if (hasStepsWithRecollectingStatus && flow === 'contractors') {
          await createAsyncAction(
            dispatch,
            SendRemainderContractor.request({
              partnerDriverId: contractorDetails.id,
            }),
          )
        }
        showToast(
          "The reminder has been resent to the contractor's email address and phone number!",
        )
      } catch (err) {
        handleApiError(err)
        showToast('Failed to resend the reminder.')
      } finally {
        setIsLoading(false)
      }
    }, [contractorDetails, flow])

    const mainActionButton = useMemo(() => {
      if (contractorDetails.collectedDataStatus === OnboardingStatus.Failed) {
        return <UnrejectContractor onUnreject={handleUnrejectWorker} />
      } else if (flow === 'onboardings') {
        if (contractorDetails.collectedDataStatus !== OnboardingStatus.Completed) {
          return <RejectContractor onReject={handleRejectWorker} isLoading={isLoading} />
        }
      } else {
        if (contractorDetails.isActive) {
          return (
            <div css={tw`flex flex-row gap-2`}>
              <div css={tw`flex-[3]`}>
                <DeactivateContractor onDeactivate={handleDeactivateWorker} />
              </div>
              <div css={tw`flex-[2]`}>
                <SetDeactivationDate contractorDetails={contractorDetails} onRefresh={onRefresh} />
              </div>
            </div>
          )
        } else {
          return <ActivateContractor onActivate={handleActivateWorker} />
        }
      }

      return null
    }, [
      contractorDetails.collectedDataStatus,
      contractorDetails.isActive,
      contractorDetails,
      flow,
      isLoading,
    ])

    return (
      <div css={tw`flex flex-col p-4 pb-4`}>
        <div css={tw`w-full h-px bg-[#EDEDED] mb-4`} />
        {contractorDetails.isActive && (
          <Typography
            variant="body2"
            css={
              contractorDetails.deactivationScheduledAt
                ? tw`mb-4 text-[14px] text-center font-semibold text-black`
                : tw`mb-4 text-[14px] text-center font-normal text-[#655E7C]`
            }
          >
            Deactivation date
            {contractorDetails.deactivationScheduledAt ? (
              <>
                {' • '}
                {dayjs(contractorDetails.deactivationScheduledAt).format('MMM D, YYYY')}
              </>
            ) : (
              ' not set'
            )}
          </Typography>
        )}
        {mainActionButton}
        {shouldShowResendInviteButton ? (
          <div css={tw`mt-2 w-full`}>
            <Button
              endIcon={
                isLoading ? null : (
                  <SendMajor fill={colors.PRIMARY_PURPLE_500} width={20} height={20} />
                )
              }
              css={tw`h-[36px] flex gap-0`}
              color="light"
              size="small"
              fullWidth
              onClick={handleResendInvite}
              loading={isLoading}
              disabled={statusIsUpdating}
            >
              Resend an invite
            </Button>
            <div css={tw`mb-2`} />
          </div>
        ) : null}
      </div>
    )
  },
)

export { ContractorActions }
